import { Injectable } from '@angular/core';
import { AVAILABLE_DUTY_TYPES } from '@app-asset-config/common/asset-configuration.constants';
import { BehaviorSubject } from 'rxjs';
import { io, ManagerOptions, Socket, SocketOptions } from 'socket.io-client';
import { AccessService } from '../access/access.service';
import { DataService } from '../data/data.service';
import { AssetState, DeviceState, DeviceStateInput } from '@app-live-view/models/live-view.model';
import { DEVICE_GPS_UPDATE_TIMER_MIN, TRIP_STATUS_UPDATE_TIMER_MIN } from '@app-live-view/constants/live-view.constants';
import { StartupService } from '../startup/startup.service';
import { API } from '@app-core/constants/api.constants';
import { takeUntil } from 'rxjs/operators';

export interface AuthOptions {
  deviceId: string;
  partition: number;
}

@Injectable({
  providedIn: 'root',
})
export class LiveTelematicsService {
  private defaultSocketConnectionOptions: Partial<ManagerOptions & SocketOptions> = {
    path: '/ws',
    extraHeaders: {},
    autoConnect: false,
    withCredentials: true,
    query: {},
    transports: ['websocket', 'webtransport', 'polling'],
    closeOnBeforeunload: true,
    rememberUpgrade: true,
  };
  private _zoomedOutViewSocket: Socket;
  private _deviceLevelViewSocket: Socket;
  private currentFleet: string;
  private isLiveTelematicsEnabled$: BehaviorSubject<boolean> = new BehaviorSubject(true);

  constructor(private accessService: AccessService, private dataService: DataService, private startupService: StartupService) {
    this.dataService._currentFleet.pipe(takeUntil(this.startupService.destroy$)).subscribe((value: string) => {
      if (value) {
        this.currentFleet = value;
        this.disconnectSocketConnections();
        this.initializeSocketConnections();
      }
    });

    this.startupService.fleetSdkConfig.pipe(takeUntil(this.startupService.destroy$)).subscribe((config) => {
      // ignore if config is not loaded yet

      if (Object.keys(config).length > 0) {
        const liveTelematicsEnabledConfigStatus =
          config &&
          AVAILABLE_DUTY_TYPES.map(({ key: dutyType }) => {
            return !!(config[dutyType] || {}).enableLiveTelematics;
          }).some((e) => !!e);

        const fleetDetails = this.startupService.fleetDetails.getValue();
        const { preferences } = fleetDetails || {};
        const fleetPref = preferences?.livestreaming?.enabled || false;

        const status = liveTelematicsEnabledConfigStatus && fleetPref;
        this.isLiveTelematicsEnabled$.next(status);
      }
    });
  }

  private disconnectSocketConnections() {
    if (this._zoomedOutViewSocket && this._zoomedOutViewSocket.connected) {
      this._zoomedOutViewSocket.disconnect();
    }
    if (this._deviceLevelViewSocket && this._deviceLevelViewSocket.connected) {
      this._deviceLevelViewSocket.disconnect();
    }
  }

  private initializeSocketConnections() {
    const token = this.accessService.getLoginInfo().token;
    this.defaultSocketConnectionOptions.extraHeaders = {
      'x-access-token': token,
    };
    this.defaultSocketConnectionOptions.auth = {
      'x-access-token': token,
    };
    this.defaultSocketConnectionOptions.query = {
      fleetId: this.currentFleet,
    };
    this._zoomedOutViewSocket = io(API.INTERMEDIATE_SERVER_URL + '/v2/zoomed-out-view', {
      ...this.defaultSocketConnectionOptions,
    });
    this._deviceLevelViewSocket = io(API.INTERMEDIATE_SERVER_URL + '/device-level-view', {
      ...this.defaultSocketConnectionOptions,
    });
  }

  public get zoomedOutViewSocket(): Socket {
    return this._zoomedOutViewSocket;
  }

  public get deviceLevelViewSocket(): Socket {
    return this._deviceLevelViewSocket;
  }

  get isLiveTelematicsEnabled() {
    return this.isLiveTelematicsEnabled$;
  }

  public getLiveDeviceState({ statusUpdateTimestampUTC, lastPingTimestampUTC, ongoing = false }: DeviceStateInput): DeviceState {
    if (!ongoing) {
      return DeviceState.Inactive;
    }

    if (lastPingTimestampUTC) {
      const minGpsUpdateUTC = new Date(Date.now() - DEVICE_GPS_UPDATE_TIMER_MIN * 60 * 1000).toISOString();
      const lastGpsReceivedOlder = new Date(lastPingTimestampUTC).getTime() < new Date(minGpsUpdateUTC).getTime();
      if (lastGpsReceivedOlder) {
        return DeviceState.Amber;
      }
      return DeviceState.Active;
    }

    if (statusUpdateTimestampUTC) {
      const minTripStatusUpdateUTC = new Date(Date.now() - TRIP_STATUS_UPDATE_TIMER_MIN * 60 * 1000).toISOString();
      const tripStatusUpdatedOlder = new Date(statusUpdateTimestampUTC).getTime() < new Date(minTripStatusUpdateUTC).getTime();
      if (tripStatusUpdatedOlder) {
        return DeviceState.Amber;
      }
      return DeviceState.Active;
    }
    return DeviceState.Amber;
  }

  public getLiveDeviceState2({ statusUpdateTimestampUTC, lastPingTimestampUTC, ongoing = false }: DeviceStateInput): AssetState {
    if (!ongoing) {
      return AssetState.Parked;
    }

    if (lastPingTimestampUTC) {
      const minGpsUpdateUTC = new Date(Date.now() - DEVICE_GPS_UPDATE_TIMER_MIN * 60 * 1000).toISOString();
      const lastGpsReceivedOlder = new Date(lastPingTimestampUTC).getTime() < new Date(minGpsUpdateUTC).getTime();
      if (lastGpsReceivedOlder) {
        return AssetState.Inactive;
      }
      return AssetState.Active;
    }

    if (statusUpdateTimestampUTC) {
      const minTripStatusUpdateUTC = new Date(Date.now() - TRIP_STATUS_UPDATE_TIMER_MIN * 60 * 1000).toISOString();
      const tripStatusUpdatedOlder = new Date(statusUpdateTimestampUTC).getTime() < new Date(minTripStatusUpdateUTC).getTime();
      if (tripStatusUpdatedOlder) {
        return AssetState.Inactive;
      }
      return AssetState.Active;
    }
    return AssetState.Inactive;
  }
}
